import React from 'react';
import { Link } from 'react-router-dom';
import MasculinityGauge from './MasculinityGauge';
import { formatResultDate } from '../../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { localizeFacePersona } from '../../i18n/facePersona';

const INDICATORS = [
    'Face Aspect Ratio',
    'Cheek to Forehead Ratio',
    'Jaw to Cheek Ratio',
    'Middle to Cheek Ratio',
    'Lower Face Ratio',
];

const FaceAnalysisCard = ({ faceResult }) => {
    const { t } = useTranslation();
    const detailUrl = faceResult?.id ? `/mypage/face-results/${faceResult.id}` : '/info';
    const persona = localizeFacePersona(faceResult?.persona, t);
    const labels = faceResult?.labels || {};
    const dateLabel = formatResultDate(faceResult?.created_at || faceResult?.timestamp);

    return (
        <Link to={detailUrl} style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {faceResult ? (
                    <>
                        <MasculinityGauge
                            percentile={faceResult?.masculinity_percentile}
                            label={t('mypage.results.face.teto_score', { score: Math.round(Number(faceResult?.masculinity_score) || 0) })}
                        />

                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '1rem',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '12px',
                            border: '1px solid rgba(255, 255, 255, 0.1)',
                            textAlign: 'center',
                            marginBottom: '0.5rem'
                        }}>
                            {persona ? (
                                <>
                                    <span style={{ color: '#94a3b8', fontSize: '0.8rem', marginBottom: '0.3rem', textTransform: 'uppercase' }}>
                                        Attraction Persona
                                    </span>
                                    <span style={{
                                        color: '#f472b6',
                                        fontWeight: '800',
                                        fontSize: '1.2rem',
                                        background: 'linear-gradient(90deg, #f472b6, #a78bfa)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        marginBottom: '0.5rem'
                                    }}>
                                        {persona?.title || 'Unknown Persona'}
                                    </span>
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '0.5rem' }}>
                                        <span style={{
                                            fontSize: '0.75rem',
                                            background: 'rgba(96, 165, 250, 0.2)',
                                            color: '#60a5fa',
                                            padding: '0.2rem 0.6rem',
                                            borderRadius: '6px'
                                        }}>
                                            {t(`persona_traits.base.${String(persona?.base_type || '').toUpperCase()}`, { defaultValue: persona?.base_type || '-' })}
                                        </span>
                                        <span style={{ color: '#64748b', fontSize: '0.75rem' }}>+</span>
                                        <span style={{
                                            fontSize: '0.75rem',
                                            background: 'rgba(248, 113, 113, 0.2)',
                                            color: '#f87171',
                                            padding: '0.2rem 0.6rem',
                                            borderRadius: '6px'
                                        }}>
                                            {t(`persona_traits.spice.${String(persona?.spice_type || '').toUpperCase()}`, { defaultValue: persona?.spice_type || '-' })}
                                        </span>
                                    </div>
                                </>
                            ) : (
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                    <span style={{ color: '#cbd5e1', fontSize: '0.9rem' }}>{t('mypage.results.face.combo_type')}</span>
                                    <span style={{ color: '#60a5fa', fontWeight: 'bold' }}>{faceResult?.combo_id || 'Unknown'}</span>
                                </div>
                            )}
                        </div>

                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gap: '0.5rem',
                            fontSize: '0.8rem'
                        }}>
                            {INDICATORS.map((key) => {
                                const rawValue = labels?.[key] || '-';
                                const translatedValue = t(`face_result.values.${rawValue}`, { defaultValue: rawValue });
                                return (
                                    <div
                                        key={key}
                                        style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                            padding: '0.5rem',
                                            borderRadius: '8px',
                                            display: 'flex',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <span style={{ color: '#94a3b8' }}>{t(`face_result.labels.${key}`, { defaultValue: key })}</span>
                                        <span style={{ color: rawValue === 'HIGH' ? '#f87171' : rawValue === 'LOW' ? '#60a5fa' : '#fbbf24', fontWeight: 'bold' }}>
                                            {translatedValue}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        {dateLabel ? (
                            <div style={{ textAlign: 'right' }}>
                                <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                    {dateLabel}
                                </span>
                            </div>
                        ) : null}
                    </>
                ) : (
                    <div style={{
                        color: '#64748b',
                        fontStyle: 'italic',
                        textAlign: 'center',
                        padding: '1rem'
                    }}>
                        {t('mypage.results.face.empty')}
                    </div>
                )}
            </div>
        </Link>
    );
};

export default FaceAnalysisCard;
